import { Typography, Table, Card, Space, Tag, Divider } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { PageHeader } from '@/components/PageHeader';

const { Title, Text, Paragraph, Link } = Typography;

const DEEP_LINK_BASE = `${import.meta.env.VITE_API_URL ?? ''}/app`;

const deepLinkRoutes = [
  {
    pattern: '/app/report/:reportId',
    redirectsTo: '/report-detail/:reportId',
    auth: true,
    example: `${DEEP_LINK_BASE}/report/abc123`,
    description: 'Ouvre le détail d\'un signalement dans l\'app cliente.',
  },
  {
    pattern: '/app/intervention/:interventionId',
    redirectsTo: '/intervention',
    auth: true,
    example: `${DEEP_LINK_BASE}/intervention/xyz789`,
    description: 'Ouvre la page d\'intervention dans l\'app cliente.',
  },
  {
    pattern: '/app/alert/:alertId',
    redirectsTo: '/alert-detail/:alertId',
    auth: true,
    example: `${DEEP_LINK_BASE}/alert/def456`,
    description: 'Ouvre le détail d\'une alerte dans l\'app cliente.',
  },
];

const preAuthRoutes = [
  {
    pattern: '/app/__/auth/links',
    purpose: 'Callback de connexion par lien email (Firebase)',
    auth: false,
    buffered: true,
  },
  {
    pattern: '/app/agent-invite',
    purpose: 'Landing d\'invitation agent (inscription Pro)',
    auth: false,
    buffered: true,
  },
];

const routeColumns = [
  {
    title: 'Pattern',
    dataIndex: 'pattern',
    key: 'pattern',
    render: (text: string) => <code style={{ fontSize: 13 }}>{text}</code>,
  },
  {
    title: 'Redirige vers',
    dataIndex: 'redirectsTo',
    key: 'redirectsTo',
    render: (text: string) => <code style={{ fontSize: 13 }}>{text}</code>,
  },
  {
    title: 'Auth requise',
    dataIndex: 'auth',
    key: 'auth',
    render: (auth: boolean) => (
      <Tag color={auth ? 'orange' : 'green'}>{auth ? 'Oui' : 'Non'}</Tag>
    ),
  },
  {
    title: 'Exemple',
    dataIndex: 'example',
    key: 'example',
    render: (url: string) => (
      <Link href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </Link>
    ),
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
  },
];

const preAuthColumns = [
  {
    title: 'Pattern',
    dataIndex: 'pattern',
    key: 'pattern',
    render: (text: string) => <code style={{ fontSize: 13 }}>{text}</code>,
  },
  {
    title: 'Utilité',
    dataIndex: 'purpose',
    key: 'purpose',
  },
  {
    title: 'Auth requise',
    dataIndex: 'auth',
    key: 'auth',
    render: (auth: boolean) => (
      <Tag color={auth ? 'orange' : 'green'}>{auth ? 'Oui' : 'Non'}</Tag>
    ),
  },
  {
    title: 'Buffered',
    dataIndex: 'buffered',
    key: 'buffered',
    render: (buffered: boolean) =>
      buffered ? <Tag color="blue">Oui</Tag> : <Tag>Non</Tag>,
  },
];

const allClientRoutes = [
  { path: '/', page: 'MobileLoadingPage', deepLink: true, note: 'Point d\'entrée / buffer redirect' },
  { path: '/onboarding', page: 'ClientOnboardingFeaturePage', deepLink: false },
  { path: '/login', page: 'MobileLoginPage', deepLink: false },
  { path: '/signup', page: 'ClientSignupPage', deepLink: false },
  { path: '/forgot-password', page: 'ForgotPasswordPage', deepLink: false },
  { path: '/profile-creation', page: 'ClientProfileCreationPage', deepLink: false },
  { path: '/sms-verification', page: 'ClientSmsVerificationPage', deepLink: false },
  { path: '/app/__/auth/links', page: 'EmailLinkHandlerPage', deepLink: true },
  { path: '/app/agent-invite', page: 'AgentInviteLandingPage', deepLink: true },
  { path: '/home/tab', page: 'MobileHomePage', deepLink: false },
  { path: '/home/map', page: 'MobileMapPage', deepLink: false },
  { path: '/home/reports', page: 'MobileReportPage', deepLink: false },
  { path: '/home/account', page: 'MobileAccountPage', deepLink: false },
  { path: '/intervention', page: 'MobileInterventionPage', deepLink: false },
  { path: '/report-detail/:reportId', page: 'ClientReportDetailPage', deepLink: false },
  { path: '/alert-detail/:alertId', page: 'Placeholder', deepLink: false },
  { path: '/notification-center', page: 'MobileNotificationCenterPage', deepLink: false },
  { path: '/feed-detail', page: 'ClientFeedDetailPage', deepLink: false },
];

const allClientColumns = [
  {
    title: 'Path',
    dataIndex: 'path',
    key: 'path',
    render: (text: string) => <code style={{ fontSize: 13 }}>{text}</code>,
  },
  { title: 'Page', dataIndex: 'page', key: 'page' },
  {
    title: 'Deep link?',
    dataIndex: 'deepLink',
    key: 'deepLink',
    render: (v: boolean) => v ? <Tag color="blue">Oui</Tag> : <Tag>Non</Tag>,
  },
  { title: 'Note', dataIndex: 'note', key: 'note', render: (t?: string) => t ?? '—' },
];

export function DeepLinksDocsPage() {
  return (
    <div>
      <PageHeader
        title="Deep Links — App Cliente"
        subtitle="Documentation des routes deep link de l'application Wisecure"
      />

      <Paragraph>
        L'application cliente utilise <strong>GoRouter</strong> pour la navigation et supporte le deep linking
        via des routes de redirection dédiées et un <code>PendingDeepLinkService</code> qui bufferise les URIs
        entrants jusqu'à l'initialisation complète de l'app.
      </Paragraph>

      <Card style={{ marginBottom: 24 }}>
        <Title level={4}>Fonctionnement général</Title>
        <ol style={{ paddingLeft: 20 }}>
          <li>Un deep link URI arrive (tap sur notification, universal link, etc.)</li>
          <li>Si l'app <strong>n'est pas encore initialisée</strong>, l'URI est sauvegardé dans <code>PendingDeepLinkService</code> et l'utilisateur est redirigé vers l'écran de chargement (<code>/</code>)</li>
          <li>Une fois l'initialisation terminée, <code>_navigateAfterInit</code> vérifie s'il y a un deep link en attente et navigue via <code>GoRouter.of(context).go(pathWithQuery)</code></li>
          <li>Si l'app <strong>est déjà initialisée</strong>, la logique de redirection de GoRouter gère la route directement</li>
        </ol>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Title level={4}>Routes deep link principales</Title>
        <Paragraph type="secondary">
          Ces routes existent spécifiquement pour le deep linking. Elles redirigent vers leurs écrans de détail correspondants.
          Utilisables dans le champ <strong>URL d'action</strong> du feed.
        </Paragraph>
        <Table
          columns={routeColumns}
          dataSource={deepLinkRoutes}
          rowKey="pattern"
          pagination={false}
          size="small"
        />
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Title level={4}>Routes pre-auth (sans authentification)</Title>
        <Paragraph type="secondary">
          Ces routes ne nécessitent pas d'authentification et sont bufferisées si l'app n'est pas initialisée.
        </Paragraph>
        <Table
          columns={preAuthColumns}
          dataSource={preAuthRoutes}
          rowKey="pattern"
          pagination={false}
          size="small"
        />
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Title level={4}>Comment utiliser dans le feed</Title>
        <Paragraph>
          Dans le champ <strong>URL d'action</strong> d'un élément de feed, vous pouvez saisir :
        </Paragraph>
        <ul style={{ paddingLeft: 20 }}>
          <li>
            <Text strong>Une URL classique :</Text>{' '}
            <code>https://wisecure.fr/nouvelles-mesures</code> — ouvre le lien dans le navigateur
          </li>
          <li>
            <Text strong>Un deep link :</Text>{' '}
            <code>{DEEP_LINK_BASE}/report/abc123</code> — ouvre l'écran de détail du signalement dans l'app
          </li>
          <li>
            <Text strong>Un deep link avec paramètres :</Text>{' '}
            <code>{DEEP_LINK_BASE}/alert/def456</code> — ouvre l'écran de détail de l'alerte
          </li>
        </ul>

        <Divider />

        <Title level={5}>Exemples concrets</Title>
        <Space direction="vertical" size={8}>
          <div>
            <Tag color="blue">Signalement</Tag>
            <code>{DEEP_LINK_BASE}/report/clxfi123abc</code>
            <ArrowRightOutlined style={{ margin: '0 8px', color: '#999' }} />
            <Text type="secondary">Ouvre <code>/report-detail/clxfi123abc</code></Text>
          </div>
          <div>
            <Tag color="green">Intervention</Tag>
            <code>{DEEP_LINK_BASE}/intervention/clxfi456def</code>
            <ArrowRightOutlined style={{ margin: '0 8px', color: '#999' }} />
            <Text type="secondary">Ouvre la page <code>/intervention</code></Text>
          </div>
          <div>
            <Tag color="red">Alerte</Tag>
            <code>{DEEP_LINK_BASE}/alert/clxfi789ghi</code>
            <ArrowRightOutlined style={{ margin: '0 8px', color: '#999' }} />
            <Text type="secondary">Ouvre <code>/alert-detail/clxfi789ghi</code></Text>
          </div>
        </Space>
      </Card>

      <Card>
        <Title level={4}>Toutes les routes enregistrées (référence)</Title>
        <Table
          columns={allClientColumns}
          dataSource={allClientRoutes}
          rowKey="path"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
}
